import { z } from 'zod';
// =============================================================================
// State Synchronization Protocol
// =============================================================================
/**
 * Compute a simple hash/checksum of game state for verification.
 * Uses a fast, non-cryptographic hash suitable for detecting divergence.
 */
export function computeStateChecksum(state) {
    // Use JSON stringify for deterministic serialization
    // In production, you might want a faster hash like xxHash
    const json = JSON.stringify(state, Object.keys(state).sort());
    let hash = 0;
    for (let i = 0; i < json.length; i++) {
        const char = json.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    // Return as hex string for readability
    return (hash >>> 0).toString(16).padStart(8, '0');
}
/**
 * State envelope that wraps game state with versioning and checksum
 */
export const StateEnvelope = z.object({
    /** Monotonically increasing version number */
    version: z.number().int().nonnegative(),
    /** Previous version (for gap detection) */
    previousVersion: z.number().int().nonnegative().optional(),
    /** Checksum of the state for verification */
    checksum: z.string(),
    /** The actual game state */
    state: z.unknown(),
    /** Server timestamp when state was generated */
    timestamp: z.number(),
    /** Whether this is a full sync or delta update */
    updateType: z.enum(['full', 'delta']).default('delta'),
});
// Note: FullStateSync message is defined in messages.ts and exported from there
/**
 * Sync status report (for diagnostics)
 */
export const SyncStatus = z.object({
    type: z.literal('SYNC_STATUS'),
    /** Client's current state version */
    clientVersion: z.number().int().nonnegative(),
    /** Client's computed checksum */
    clientChecksum: z.string(),
    /** Whether client believes it's in sync */
    inSync: z.boolean(),
    /** Number of sync requests made this session */
    syncRequestCount: z.number().int().nonnegative(),
    /** Timestamp of last successful sync */
    lastSyncTime: z.number().optional(),
});
/**
 * Tracks sync state and detects divergence
 */
export class SyncTracker {
    version = 0;
    checksum = '';
    lastUpdateTime = Date.now();
    syncRequestCount = 0;
    inSync = true;
    config;
    staleCheckInterval = null;
    constructor(config = {}) {
        this.config = {
            checksumVerifyInterval: config.checksumVerifyInterval ?? 10,
            staleStateTimeout: config.staleStateTimeout ?? 5000,
            maxVersionGap: config.maxVersionGap ?? 3,
            onSyncLost: config.onSyncLost ?? (() => { }),
            onSyncRestored: config.onSyncRestored ?? (() => { }),
        };
    }
    /**
     * Start monitoring for stale state
     */
    startMonitoring() {
        this.stopMonitoring();
        this.staleCheckInterval = setInterval(() => {
            const timeSinceUpdate = Date.now() - this.lastUpdateTime;
            if (timeSinceUpdate > this.config.staleStateTimeout && this.inSync) {
                this.markOutOfSync('stale_state', {
                    expectedVersion: this.version + 1,
                    receivedVersion: this.version,
                    checksumMismatch: false,
                });
            }
        }, 1000);
    }
    /**
     * Stop monitoring for stale state
     */
    stopMonitoring() {
        if (this.staleCheckInterval) {
            clearInterval(this.staleCheckInterval);
            this.staleCheckInterval = null;
        }
    }
    /**
     * Process an incoming state update
     * @returns true if state should be applied, false if resync needed
     */
    processUpdate(version, checksum, previousVersion) {
        this.lastUpdateTime = Date.now();
        // Check for version gap
        const expectedVersion = this.version + 1;
        const versionGap = version - expectedVersion;
        if (versionGap > this.config.maxVersionGap) {
            this.markOutOfSync('version_gap', {
                expectedVersion,
                receivedVersion: version,
                checksumMismatch: false,
            });
            return { apply: false, needsResync: true, reason: 'version_gap' };
        }
        // If version is older than expected, it's a duplicate - ignore
        if (version <= this.version) {
            return { apply: false, needsResync: false, reason: 'duplicate' };
        }
        // Version gap within tolerance - apply but may need resync later
        if (versionGap > 0 && previousVersion !== undefined && previousVersion !== this.version) {
            // We missed some updates but within tolerance
            // Apply optimistically but flag for checksum verification
        }
        // Periodic checksum verification
        if (version % this.config.checksumVerifyInterval === 0 &&
            this.checksum &&
            checksum !== this.checksum) {
            this.markOutOfSync('checksum_mismatch', {
                expectedVersion: this.version,
                receivedVersion: version,
                checksumMismatch: true,
            });
            return { apply: false, needsResync: true, reason: 'checksum_mismatch' };
        }
        // Update tracking
        this.version = version;
        this.checksum = checksum;
        // If we were out of sync, mark as restored
        if (!this.inSync) {
            this.inSync = true;
            this.config.onSyncRestored();
        }
        return { apply: true, needsResync: false };
    }
    /**
     * Handle full state sync response
     */
    handleFullSync(version, checksum) {
        this.version = version;
        this.checksum = checksum;
        this.lastUpdateTime = Date.now();
        if (!this.inSync) {
            this.inSync = true;
            this.config.onSyncRestored();
        }
    }
    /**
     * Mark as out of sync and trigger callback
     */
    markOutOfSync(reason, details) {
        if (this.inSync) {
            this.inSync = false;
            this.syncRequestCount++;
            this.config.onSyncLost(reason, details);
        }
    }
    /**
     * Get current sync status
     */
    getStatus() {
        return {
            version: this.version,
            checksum: this.checksum,
            inSync: this.inSync,
            syncRequestCount: this.syncRequestCount,
            lastUpdateTime: this.lastUpdateTime,
        };
    }
    /**
     * Reset tracker state
     */
    reset() {
        this.version = 0;
        this.checksum = '';
        this.lastUpdateTime = Date.now();
        this.inSync = true;
    }
    /**
     * Clean up resources
     */
    dispose() {
        this.stopMonitoring();
    }
}
/**
 * Create a state envelope with computed checksum
 */
export function createStateEnvelope(state, version, previousVersion, updateType = 'delta') {
    return {
        version,
        previousVersion,
        checksum: computeStateChecksum(state),
        state,
        timestamp: Date.now(),
        updateType,
    };
}
/**
 * Verify a state envelope's checksum
 */
export function verifyStateChecksum(envelope) {
    const computed = computeStateChecksum(envelope.state);
    return computed === envelope.checksum;
}
//# sourceMappingURL=sync.js.map